import moment from 'moment';
import { orderBy } from 'lodash-es';
import { createSelector } from '@ngrx/store';
import { IAppState } from '../store.dto';
import {
  IScheduleItem,
  IScheduledJobColumn,
  IJobSummaryDto,
  IAvailableJob,
  RangeValue,
  ScheduledJobGridItem,
  ISchedule,
  ColorRule,
} from 'src/app/core/dto';
import { evaluateGroupColor } from '../../color-rules/color-rules';
import { appendSummarizer2, JobsInFlightState } from './reducers';
import {
  applyOverlayToAvailable,
  applyOverlayToSchedules,
  pendingOrdIds,
} from './schedule-overlay';
import {
  JOB_CARD_ROLE,
  JOB_ROLE,
  MATERIAL_ROLE,
  SCHEDULER_NEUTRALS,
  SchedulerRole,
  TOOLING_ROLE,
} from 'src/app/core/scheduler-roles';

export const selectMachines = (store: IAppState) => store.data.collections.Machine;
export const selectAvailableJobColumns = (store: IAppState) => store.data.AvailableJobColumns;
export const selectScheduledJobColumns = (store: IAppState) => store.data.ScheduledJobColumns;
export const selectScheduledJobs = (store: IAppState) => store.data.collections.Schedule;
export const selectAvailableJobs = (store: IAppState) => store.data.collections.AvailableJob;
export const selectAlerts = (store: IAppState) => store.data.collections.Alerts;

export const selectJobsInFlight = (store: IAppState) => store.data.JobsInFlight;
export const selectSelectedJobs = (store: IAppState) => store.data.SelectedJobs;
export const selectSummarizedJobs = (store: IAppState) => store.data.SummarizedJobs;

export const selectSchedule = (store: IAppState) => store.data.collections.Schedule;

export const selectScheduleOverlay = (store: IAppState) => store.data.ScheduleOverlay;

/** The Schedule docs as the scheduler grid shows them: with the user's pending moves applied. */
export const selectScheduleWithOverlay = createSelector(
  selectSchedule,
  selectScheduleOverlay,
  (schedules, overlay) => applyOverlayToSchedules(schedules, overlay.ops)
);

/** The AvailableJob rows as the scheduler grid shows them: with the user's pending moves applied. */
export const selectAvailableJobsWithOverlay = createSelector(
  selectAvailableJobs,
  selectScheduleOverlay,
  (availableJobs, overlay) => applyOverlayToAvailable(availableJobs, overlay.ops)
);

/** Jobs whose move hasn't been confirmed yet. Further actions on them wait until it is. */
export const selectPendingScheduleOrdIds = createSelector(selectScheduleOverlay, (overlay) =>
  pendingOrdIds(overlay.ops)
);

export type ScheduleSavingSummary = { count: number; unconfirmed: boolean };

/** How many of a machine's moved jobs are still being saved, for the scheduler header. */
export const selectScheduleSavingSummary = (machineNumber: number) =>
  createSelector(selectScheduleOverlay, (overlay): ScheduleSavingSummary => {
    const ops = overlay.ops.filter((op) => op.machineNumber === machineNumber);
    return {
      count: ops.reduce((sum, op) => sum + op.ordIds.length, 0),
      unconfirmed: ops.some((op) => op.state === 'unconfirmed'),
    };
  });

type AssignedJobsGridDataType = ReturnType<ReturnType<typeof AssignedJobsGridData>>;

export const AssignedJobsGridData =
  (machineNumber: number) =>
  (scheduledJobs: ISchedule[], selectedJobs: number[], jobsInFlight: JobsInFlightState) => {
    const schedule = scheduledJobs.find((s) => s.machineNumber === machineNumber);

    const sJobs = !schedule ? [] : schedule.sequence;
    // Sets, not Array.includes per row: select-all on a 5,000-job schedule was 25M comparisons.
    const selected_ = new Set(selectedJobs);
    const fliers = new Set(jobsInFlight.fliers);

    const gridData = sJobs
      // How do I make this automatically be of type (ISchedule & IJobSummaryDto)?
      .map((x) => ({
        ...x.job,
        ...x,
        completePct: x.job.completeFt / x.job.totalFt,
        remainingPieces: x.job.quantity - x.job.quantityDone,
        isSelected: selected_.has(x.ordId),
        inFlight: fliers.has(x.ordId),
        requiredDateDisplay: x.job.requiredDate
          ? moment(x.job.requiredDate).format('YYYY-MM-DD')
          : '<none>',
        importDateDisplay: x.job.importDate
          ? moment(x.job.importDate).format('YYYY-MM-DD')
          : '<none>',
        shipDateDisplay: x.job.shipDate ? moment(x.job.shipDate).format('YYYY-MM-DD') : '<none>',
      }))
      // Remove the extra job property that came from the server
      .map(({ job, ...rest }) => rest);

    const selected = gridData.filter((j) => j.isSelected);
    return {
      gridData: gridData,
      summary: {
        count: gridData.length,
        totalFt: gridData.reduce((acc, j) => j.totalFt + acc, 0),
        jobIds: gridData.map((j) => j.ordId),
        heldJobIds: gridData.filter((j) => j.hold).map((j) => j.ordId),
        notHeldJobIds: gridData.filter((j) => !j.hold).map((j) => j.ordId),
      },
      selectedSummary: {
        count: selected.length,
        totalFt: selected.reduce((acc, j) => j.totalFt + acc, 0),
        jobIds: selected.map((j) => j.ordId),
        heldJobIds: selected.filter((j) => j.hold).map((j) => j.ordId),
        notHeldJobIds: selected.filter((j) => !j.hold).map((j) => j.ordId),
      },
    };
  };

export type SJT = {
  key: string | RangeValue;
  keyColumn: { fieldName: string; role: SchedulerRole };
  jobs: (IScheduleItem & IJobSummaryDto & { isSelected: boolean })[];
  items: SJT[];
  jobIds: number[];
  id: string;
  isSelected: boolean;
  href: string;
  srefObj?: string;
  isSummaryRow: boolean;
  summary: any;
  /** Cell background: color-rule result, or the role's pale fill. */
  background: string;
  /** Text color for `background`. */
  ink: string;
  /**
   * The role's saturated leading edge, but only on the first cell of a run of
   * that role in this row. Empty everywhere else, so a role whose columns are
   * not contiguous reads as one band per run.
   */
  rail: string;
  /** Every job under this node is a provisional (not yet confirmed) move. */
  provisional?: boolean;
};

/**
 * Columns that carry a role but are not rendered as a tinted cell — the
 * on-machine marker is a 7px gutter, not a value. They must not consume a run,
 * or the first *visible* tooling column would lose its rail.
 */
const isDecorativeColumn = (column: { fieldName: string }) =>
  column.fieldName === 'isOnMachine';

/**
 * Fill / ink / rail for one cell.
 *
 * Color rules override the *fill* only; the rail stays the role's, so a rule
 * can recolor a group without moving which cell owns the run's leading edge.
 * A rule-painted cell falls back to primary ink, because the rule's color is
 * arbitrary and the role's ink is only guaranteed legible on the role's fill.
 */
const cellColors = (
  role: SchedulerRole,
  ruleColor: string | null,
  isRunStart: boolean,
  isSummaryRow: boolean
) => ({
  // What a summary row does with its fill is a theme decision — one direction
  // drops it to white and leans on the rail, the other keeps it.
  background: ruleColor ?? (isSummaryRow ? role.summaryFill : role.fill),
  ink: ruleColor ? SCHEDULER_NEUTRALS.primaryText : role.ink,
  rail: isRunStart ? role.rail : '',
});

/** Groups consecutive items with the same key. */
function groupWhile<T, TKey>(array: T[], keyselector: (t: T) => TKey) {
  const groups: { key: TKey; items: T[] }[] = [];
  for (const cur of array) {
    const key = keyselector(cur);
    const end = groups[groups.length - 1];
    if (end && end.key === key) {
      end.items.push(cur);
    } else {
      // push, not [...acc, group]: the last column (`id`) makes one group per job, so copying the
      // accumulator per group was quadratic in the size of a material/tooling run.
      groups.push({ key, items: [cur] });
    }
  }
  return groups;
}
function buildSummaryItem(
  items: ScheduledJobGridItem[],
  columns: IScheduledJobColumn[],
  rules: ColorRule[],
  previousRole: SchedulerRole | null
): SJT {
  let [keyColumn, ...remainingColumns] = columns;
  const key = keyColumn.summarizer(items, keyColumn);
  const decorative = isDecorativeColumn(keyColumn);
  const isRunStart = !decorative && keyColumn.role.id !== previousRole?.id;
  const carriedRole = decorative ? previousRole : keyColumn.role;
  return {
    key: key,
    keyColumn: keyColumn,
    jobs: items,
    items:
      remainingColumns.length > 0
        ? [buildSummaryItem(items, remainingColumns, rules, carriedRole)]
        : [],
    jobIds: items.map((j) => j.ordId),
    id: key.toString() + columns.length + items.length, // lengths are just because I feel like I need some uniqueness on this id
    isSelected: items.every((j) => j.isSelected),
    provisional: items.every((j) => j.provisional),
    href: '',
    isSummaryRow: true,
    summary: toSummaryModel(items),
    ...cellColors(
      keyColumn.role,
      evaluateGroupColor(items, keyColumn.fieldName, rules),
      isRunStart,
      true
    ),
  };
}

const toSummaryModel = (jobs: ScheduledJobGridItem[]) => ({
  count: jobs.length,
  totalFt: jobs.reduce((sum, j) => sum + j.totalFt, 0),
});

export type ScheduleJobsGridDataModel = ReturnType<typeof mapToScheduleJobsGridDataModel>;

const buildScheduledJobsTree = (
  jobs: ScheduledJobGridItem[],
  jobsTitles: IScheduledJobColumn[],
  summarizedJobs: ReadonlySet<number>,
  rules: ColorRule[],
  previousRole: SchedulerRole | null = null
): SJT[] => {
  if (jobsTitles.length > 0) {
    let [keyColumn, ...remainingColumns] = jobsTitles;
    // A rail marks the start of a run of one role's columns. Walking the
    // ordered column list per row (rather than flagging columns statically)
    // is what makes a non-contiguous role read as two bands.
    const decorative = isDecorativeColumn(keyColumn);
    const isRunStart = !decorative && keyColumn.role.id !== previousRole?.id;
    const carriedRole = decorative ? previousRole : keyColumn.role;
    let groups = groupWhile(jobs, (j) => j[keyColumn.fieldName]);
    return groups.map((grp, i) => {
      const jobIds = grp.items.map((j) => j.ordId);
      const isSummaryRow = jobIds.every((jobid) => summarizedJobs.has(jobid));
      return {
        key: grp.key,
        keyColumn,
        jobs: grp.items,
        items: isSummaryRow
          ? [buildSummaryItem(grp.items, remainingColumns, rules, carriedRole)]
          : buildScheduledJobsTree(
              grp.items,
              remainingColumns,
              summarizedJobs,
              rules,
              carriedRole
            ),
        jobIds: jobIds,
        id: grp.key + ':' + i,
        isSelected: grp.items.every((j) => j.isSelected),
        provisional: grp.items.every((j) => j.provisional),
        href:
          keyColumn.fieldName === 'orderCode'
            ? `/orders/${grp.items[0].ordId}`
            : keyColumn.fieldName === 'materialCode'
            ? `/inventory/coil-types/${grp.items[0].materialCode}`
            : '',
        srefObj:
          keyColumn.fieldName === 'orderCode'
            ? `/orders/${grp.items[0].ordId}`
            : keyColumn.fieldName === 'materialCode'
            ? `/inventory/coil-types/${grp.items[0].materialCode}`
            : undefined,
        isSummaryRow: isSummaryRow,
        summary: toSummaryModel(grp.items),
        ...cellColors(
          keyColumn.role,
          evaluateGroupColor(grp.items, keyColumn.fieldName, rules),
          isRunStart,
          isSummaryRow
        ),
      };
    });
  } else return [];
};
/** True once a job's effective due time (`IJobSummaryDto.dueAt`) has passed. No due time is never late. */
export const isPastDue = (dueAt: string | null | undefined, now: number = Date.now()): boolean =>
  !!dueAt && new Date(dueAt).getTime() < now;

export const mapToScheduleJobsGridDataModel = (
  jobs: AssignedJobsGridDataType,
  columns: IScheduledJobColumn[],
  summarizedJobs: number[],
  colorRules: ColorRule[] = []
) => {
  let dateClosure: Date = null;
  const summarized = new Set(summarizedJobs);
  let sortedJobs = orderBy(jobs.gridData, 'sequenceNum', 'asc')
    .map((job, i) => {
      return {
        ...job,
        mainIndex: i,
        // dueAt is the effective (truck route) due time; requiredDate's midnight made jobs late all day.
        pastDueDate: isPastDue(job.dueAt),
        iconColor: 'red-fg',
        warningText: 'Warning: Job is scheduled to be completed after due date!',
        pastDueText: 'Job is Late!',
      };
    })
    .map((j) => {
      if (j.provisional) {
        // Not estimated yet: no completion time, and it must not start a new date heading.
        return { ...j, dateChange: undefined };
      }
      const completionDate = moment(j.completionDate).toDate();
      return {
        ...j,
        dateChange:
          // We only want to test that the _date_ portion changed, not
          // the time. getDate is kinda cheating, but with this data
          // we know date changes will be consecutive days. getDate
          // ought to be adequate.
          completionDate?.getDate() === dateClosure?.getDate()
            ? undefined
            : (dateClosure = completionDate),
      };
    });

  function newColumn(init: {
    fieldName: keyof ScheduledJobGridItem;
    role: SchedulerRole;
  }): IScheduledJobColumn {
    return {
      ...init,
      name: init.fieldName,
      isChecked: false,
      displayName: init.fieldName,
      units: '',
      summarizer: (items, column) => '<< no summary >>',
    };
  }
  return {
    jobsTree: buildScheduledJobsTree(
      sortedJobs,
      [
        newColumn({
          fieldName: 'isOnMachine',
          role: TOOLING_ROLE,
        }),
        {
          ...newColumn({
            fieldName: 'toolingCode',
            role: TOOLING_ROLE,
          }),
          summarizer: appendSummarizer2,
        },
        {
          ...newColumn({
            fieldName: 'materialCode',
            role: MATERIAL_ROLE,
          }),
          summarizer: appendSummarizer2,
        },
        // The narrow tree renders this column as the job card itself, which is
        // white rather than tinted.
        newColumn({ fieldName: 'id', role: JOB_CARD_ROLE }), // using job id as the final column means there is exactly one job in the final items array
      ],
      summarized,
      colorRules
    ),
    jobsTreeWide: buildScheduledJobsTree(
      sortedJobs,
      [
        // We always need to start with `isOnMachine` and end with `id` (at least for now)
        newColumn({ fieldName: 'isOnMachine', role: TOOLING_ROLE }),
        ...columns.filter((x) => x.isChecked),
        newColumn({ fieldName: 'id', role: JOB_ROLE }), // using job id as the final column means there is exactly one job in the final items array
      ],
      summarized,
      colorRules
    ),
    machineJobsCount: jobs.gridData.filter((j) => j.isOnMachine).length,
    machineJobsFt: jobs.gridData
      .filter((j) => j.isOnMachine)
      .reduce((acc, current) => acc + current.remainingFt, 0),
    summary: jobs.summary,
    selectedSummary: jobs.selectedSummary,
    dates: jobs.gridData.filter((j) => !j.provisional).map((j) => j.completionDate),
  };
};

export type ScheduleJobsGridDataModelNew = ReturnType<typeof mapToScheduleJobsGridDataModel>;

export type JobSetSummary = {
  count: number;
  totalFt: number;
  jobIds: number[];
  heldJobIds: number[];
  notHeldJobIds: number[];
};

export type AvailableJobGridItem = IAvailableJob & {
  isSelected: boolean;
  totalFt: number;
  requiredDateDisplay: string;
  importDateDisplay: string;
  shipDateDisplay: string;
  inFlight: boolean;
  isSummarized?: boolean;
  patternNotDefined?: boolean;
};

export const AvailableJobsGridData =
  (machineId: number) =>
  (
    avaJobs: IAvailableJob[],
    selectedJobs: number[],
    jobsInFlight: JobsInFlightState,
    summarizedJobs: number[]
  ) => {
    const availableJobs = avaJobs.filter((sj) => sj.machineNumber === machineId);
    // Sets, not Array.includes per row (see AssignedJobsGridData).
    const selected_ = new Set(selectedJobs);
    const fliers = new Set(jobsInFlight.fliers);
    const summarized = new Set(summarizedJobs);

    const mapFootageToDisplayPreference = (lengthFt: number) =>
      // TODO: Convert this value to the system preference for footage display.
      Math.round(lengthFt);

    const gridData: AvailableJobGridItem[] = availableJobs.map((x) => ({
      ...x,
      totalFt: mapFootageToDisplayPreference(x.totalFt),
      remainingFt: mapFootageToDisplayPreference(x.remainingFt),
      requiredDateDisplay: x.requiredDate ? moment(x.requiredDate).format('YYYY-MM-DD') : '<none>',
      importDateDisplay: x.importDate ? moment(x.importDate).format('YYYY-MM-DD') : '<none>',
      shipDateDisplay: x.shipDate ? moment(x.shipDate).format('YYYY-MM-DD') : '<none>',
      isSelected: selected_.has(x.ordId),
      inFlight: fliers.has(x.ordId),
      isSummarized: summarized.has(x.ordId),
      pendingAction: false,
    }));

    const selected = gridData.filter((j) => j.isSelected);
    return {
      gridData,
      totalJobsAvailable: availableJobs.length,
      summary: {
        count: gridData.length,
        totalFt: gridData.reduce((acc, j) => j.totalFt + acc, 0),
        jobIds: gridData.map((j) => j.ordId),
        heldJobIds: gridData.filter((j) => j.hold).map((j) => j.ordId),
        notHeldJobIds: gridData.filter((j) => !j.hold).map((j) => j.ordId),
      },
      selectedSummary: {
        count: selected.length,
        totalFt: selected.reduce((acc, j) => j.totalFt + acc, 0),
        jobIds: selected.map((j) => j.ordId),
        heldJobIds: selected.filter((j) => j.hold).map((j) => j.ordId),
        notHeldJobIds: selected.filter((j) => !j.hold).map((j) => j.ordId),
      },
    };
  };

export const selectDisableScheduler = createSelector(selectAlerts, (alerts) =>
  alerts.some((a) => a.alertType === 'AgentOffline' || a.alertType === 'AgentConfiguration')
);
